import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion } from "framer-motion";
import { Pill, Check, Clock, Droplets, ChevronRight, ChevronLeft, Info, Calendar as CalendarIcon, MessageSquare, ArrowUp, AlertTriangle, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Calendar } from "../components/ui/calendar";
import { Textarea } from "../components/ui/textarea";
import { fr } from "date-fns/locale";

export default function MedicationPage() {
  const { authHeaders } = useAuth();
  const [entry, setEntry] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [status, setStatus] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showPhaseControl, setShowPhaseControl] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchEntryForDate(selectedDate);
  }, [selectedDate]);

  const fetchInitialData = async () => {
    try {
      const [scheduleRes, statusRes, calendarRes] = await Promise.all([
        axios.get(`${API}/medication/schedule`, authHeaders()),
        axios.get(`${API}/medication/status`, authHeaders()),
        axios.get(`${API}/medication/calendar`, authHeaders())
      ]);
      setSchedule(scheduleRes.data);
      setStatus(statusRes.data);
      setCalendarData(calendarRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEntryForDate = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/medication/date/${date}`, authHeaders());
      setEntry(response.data);
      setComment(response.data.comment || "");
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDose = async (doseType) => {
    const field = doseType === "20h" ? "taken_20h" : "taken_minuit";
    const newValue = !entry[field];

    setEntry(prev => ({ ...prev, [field]: newValue }));

    try {
      await axios.post(`${API}/medication/update`, {
        date: selectedDate,
        [field]: newValue
      }, authHeaders());

      // Refresh calendar data
      const calendarRes = await axios.get(`${API}/medication/calendar`, authHeaders());
      setCalendarData(calendarRes.data);

      if (newValue) {
        toast.success("N'oublie pas la goutte de clémentinier !");
      }
    } catch (e) {
      setEntry(prev => ({ ...prev, [field]: !newValue }));
      toast.error("Erreur de mise à jour");
    }
  };

  const handleSaveComment = async () => {
    try {
      await axios.post(`${API}/medication/update`, {
        date: selectedDate,
        comment: comment
      }, authHeaders());
      toast.success("Commentaire sauvegardé");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handlePhaseChange = async (action) => {
    try {
      const response = await axios.post(`${API}/medication/change-phase`, {
        action
      }, authHeaders());
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh status
        const statusRes = await axios.get(`${API}/medication/status`, authHeaders());
        setStatus(statusRes.data);
        // Refresh today's entry to get new doses
        fetchEntryForDate(selectedDate);
        setShowPhaseControl(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error("Erreur lors du changement de phase");
    }
  };

  const handleRescueDose = async () => {
    try {
      const response = await axios.post(`${API}/medication/rescue-dose`, {
        date: selectedDate
      }, authHeaders());
      
      if (response.data.success) {
        toast.success(response.data.message);
        setEntry(response.data.entry);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleCancelRescue = async () => {
    try {
      const response = await axios.post(`${API}/medication/cancel-rescue`, {
        date: selectedDate
      }, authHeaders());
      
      if (response.data.success) {
        toast.success(response.data.message);
        setEntry(response.data.entry);
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleCalendarSelect = (date) => {
    if (date) {
      setSelectedDate(date.toISOString().split('T')[0]);
    }
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Create map for calendar highlighting
  const calendarMap = {};
  calendarData.forEach(item => {
    calendarMap[item.date] = {
      taken_20h: item.taken_20h,
      taken_minuit: item.taken_minuit
    };
  });

  if (loading && !entry) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full border-4 border-[#A3E4D7]/30 border-t-[#A3E4D7]"
        />
      </div>
    );
  }

  const currentPhase = status?.phase_info;
  const overallProgress = status ? Math.min((status.current_phase / status.total_phases) * 100, 100) : 0;

  return (
    <div className="space-y-6" data-testid="medication-page">
      {/* Header with date navigation */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-nunito text-2xl font-bold text-[#2C3E50]">
            Suivi Temesta
          </h1>
          <p className="text-[#7F8C8D]">
            Tu contrôles ton rythme
          </p>
        </div>
        <Button
          onClick={() => setShowCalendar(!showCalendar)}
          variant="outline"
          data-testid="toggle-medication-calendar"
          className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
        >
          <CalendarIcon size={18} />
        </Button>
      </div>

      {/* Calendar View */}
      {showCalendar && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="bg-white rounded-3xl p-6 shadow-soft"
        >
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#A9DFBF]" />
                <span className="text-[#7F8C8D]">Complet</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#FAD7A1]" />
                <span className="text-[#7F8C8D]">Partiel</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={new Date(selectedDate)}
              onSelect={handleCalendarSelect}
              locale={fr}
              data-testid="medication-calendar"
              modifiers={{
                complete: (date) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const data = calendarMap[dateStr];
                  return data && data.taken_20h && data.taken_minuit;
                },
                partial: (date) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const data = calendarMap[dateStr];
                  return data && (data.taken_20h || data.taken_minuit) && !(data.taken_20h && data.taken_minuit);
                }
              }}
              modifiersClassNames={{
                complete: "bg-[#A9DFBF] text-[#2C3E50] hover:bg-[#A9DFBF]/80",
                partial: "bg-[#FAD7A1] text-[#2C3E50] hover:bg-[#FAD7A1]/80"
              }}
              components={{
                IconLeft: () => <ChevronLeft className="h-4 w-4" />,
                IconRight: () => <ChevronRight className="h-4 w-4" />
              }}
            />
          </div>
        </motion.div>
      )}

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button
          onClick={() => changeDate(-1)}
          variant="outline"
          size="icon"
          data-testid="prev-day-med-button"
          className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
        >
          <ChevronLeft size={18} />
        </Button>
        <span className="text-[#2C3E50] font-medium min-w-[180px] text-center">
          {isToday ? "Aujourd'hui" : formatDate(selectedDate)}
        </span>
        <Button
          onClick={() => changeDate(1)}
          variant="outline"
          size="icon"
          data-testid="next-day-med-button"
          className="rounded-full border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
        >
          <ChevronRight size={18} />
        </Button>
      </div>

      {/* Current Phase Card with Control */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-soft"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-[#FAD7A1] to-[#A3E4D7]">
            <Pill className="text-[#2C3E50]" size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-nunito font-bold text-[#2C3E50]">
                Phase {status?.current_phase || 1}
              </h2>
              <span className="text-xs bg-[#AED6F1]/30 text-[#2C3E50] px-2 py-0.5 rounded-full">
                Jour {(status?.days_in_current_phase || 0) + 1}
              </span>
            </div>
            <p className="text-[#7F8C8D] text-sm">
              {currentPhase?.description || "Phase initiale"}
            </p>
          </div>
        </div>

        {/* Phase Control Panel */}
        {showPhaseControl && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-4 p-4 rounded-2xl bg-[#FAFCFB] space-y-4"
          >
            <p className="text-sm text-[#2C3E50] font-medium text-center">
              C'est toi qui décides de ton rythme
            </p>
            
            {status?.current_phase === status?.total_phases && (
              <div className="text-center p-3 rounded-xl bg-[#A9DFBF]/30">
                <p className="text-sm text-[#2C3E50] font-medium">
                  Tu es à la dernière phase ! Bravo pour tout ce chemin parcouru !
                </p>
              </div>
            )}
          </motion.div>
        )}

        {/* Next phase button - always visible if not last phase */}
        {status?.current_phase < status?.total_phases && (
          <div className="mb-4">
            <Button
              onClick={() => handlePhaseChange("next")}
              className="w-full rounded-full bg-[#A3E4D7] hover:bg-[#A3E4D7]/80 text-[#2C3E50]"
              data-testid="next-phase-button"
            >
              <ArrowUp size={18} className="mr-2" />
              Je suis prête pour la phase suivante
            </Button>
          </div>
        )}

        {/* Overall Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#7F8C8D]">Progression globale</span>
            <span className="font-semibold text-[#2C3E50]" data-testid="overall-progress">
              Phase {status?.current_phase}/{status?.total_phases}
            </span>
          </div>
          <Progress value={overallProgress} className="h-3" />
        </div>

        {/* Current doses */}
        <div className="flex gap-4 p-4 rounded-2xl bg-[#FAFCFB]">
          <div className="flex-1 text-center">
            <p className="text-xs text-[#7F8C8D]">20h</p>
            <p className="font-semibold text-[#2C3E50]">{currentPhase?.dose_20h}</p>
          </div>
          <div className="w-px bg-[#A3E4D7]/30" />
          <div className="flex-1 text-center">
            <p className="text-xs text-[#7F8C8D]">Minuit</p>
            <p className="font-semibold text-[#2C3E50]">{currentPhase?.dose_minuit}</p>
          </div>
          <div className="w-px bg-[#A3E4D7]/30" />
          <div className="flex-1 text-center">
            <p className="text-xs text-[#7F8C8D]">Total</p>
            <p className="font-bold text-[#A3E4D7]">{currentPhase?.total}</p>
          </div>
        </div>
      </motion.div>

      {/* Today's Doses */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-3xl p-6 shadow-soft"
      >
        <h2 className="font-nunito font-bold text-[#2C3E50] mb-4">
          {isToday ? "Aujourd'hui" : formatDate(selectedDate)}
        </h2>

        <div className="space-y-4">
          {/* 20h Dose */}
          <button
            onClick={() => handleToggleDose("20h")}
            data-testid="dose-20h-button"
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-[#FAFCFB] hover:bg-[#A3E4D7]/10 transition-all duration-200"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              entry?.taken_20h ? "bg-[#A9DFBF]" : "border-2 border-[#A3E4D7]"
            }`}>
              {entry?.taken_20h && <Check className="text-[#2C3E50]" size={20} />}
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#7F8C8D]" />
                <span className="font-semibold text-[#2C3E50]">20h00</span>
              </div>
              <p className="text-[#7F8C8D] text-sm" data-testid="dose-20h-amount">
                {entry?.dose_20h}
              </p>
            </div>
          </button>

          {/* Minuit Dose */}
          <button
            onClick={() => handleToggleDose("minuit")}
            data-testid="dose-minuit-button"
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-[#FAFCFB] hover:bg-[#A3E4D7]/10 transition-all duration-200"
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              entry?.taken_minuit ? "bg-[#A9DFBF]" : "border-2 border-[#A3E4D7]"
            }`}>
              {entry?.taken_minuit && <Check className="text-[#2C3E50]" size={20} />}
            </div>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#7F8C8D]" />
                <span className="font-semibold text-[#2C3E50]">Minuit</span>
              </div>
              <p className="text-[#7F8C8D] text-sm" data-testid="dose-minuit-amount">
                {entry?.dose_minuit}
              </p>
            </div>
          </button>
        </div>

        {/* Reminder */}
        <div className="mt-4 flex items-center gap-3 p-4 rounded-2xl bg-[#FAD7A1]/20">
          <Droplets className="text-[#2C3E50] flex-shrink-0" size={20} />
          <p className="text-[#2C3E50] text-sm">
            Ajoute une goutte de clémentinier pour réduire les effets secondaires
          </p>
        </div>

        {/* Rescue dose option - always available */}
        <div className="mt-4">
          {entry?.rescue_dose ? (
            <div className="p-4 rounded-2xl bg-[#AED6F1]/20 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="text-[#2C3E50]" size={18} />
                <span className="text-sm font-medium text-[#2C3E50]">
                  Dose de secours activée (4.5mg)
                </span>
              </div>
              <Button
                onClick={handleCancelRescue}
                variant="outline"
                size="sm"
                className="w-full rounded-full border-[#7F8C8D]/30 text-[#7F8C8D]"
              >
                Annuler et reprendre la dose normale
              </Button>
            </div>
          ) : (
            <>
              <Button
                onClick={handleRescueDose}
                variant="outline"
                className="w-full rounded-full border-[#FAD7A1] text-[#2C3E50] hover:bg-[#FAD7A1]/20"
                data-testid="rescue-dose-button"
              >
                <Shield size={18} className="mr-2" />
                Dose de secours (4.5mg)
              </Button>
              <p className="text-xs text-[#7F8C8D] mt-2 text-center">
                Si symptômes de sevrage : reprend ta dose d'avant (4.5mg) pour aujourd'hui seulement
              </p>
            </>
          )}
        </div>
      </motion.div>

      {/* Comment Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-soft"
      >
        <button
          onClick={() => setShowComment(!showComment)}
          className="w-full flex items-center gap-3 text-left"
          data-testid="toggle-med-comment-button"
        >
          <MessageSquare className="text-[#7F8C8D]" size={20} />
          <span className="font-semibold text-[#2C3E50]">
            Notes du jour
          </span>
          <ChevronRight 
            size={18} 
            className={`ml-auto text-[#7F8C8D] transition-transform ${showComment ? "rotate-90" : ""}`}
          />
        </button>
        
        {showComment && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4 space-y-3"
          >
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comment te sens-tu ? Des effets secondaires ?"
              data-testid="medication-comment-textarea"
              className="min-h-[100px] rounded-2xl border-2 border-[#A3E4D7]/30 focus:border-[#A3E4D7] bg-[#FAFCFB]"
            />
            <Button
              onClick={handleSaveComment}
              data-testid="save-med-comment-button"
              className="rounded-full bg-[#A3E4D7] hover:bg-[#A3E4D7]/80 text-[#2C3E50]"
            >
              Sauvegarder
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Schedule Toggle */}
      <Button
        onClick={() => setShowSchedule(!showSchedule)}
        variant="outline"
        data-testid="toggle-schedule-button"
        className="w-full h-14 rounded-2xl border-2 border-[#A3E4D7] text-[#2C3E50] hover:bg-[#A3E4D7]/10"
      >
        <Info size={18} className="mr-2" />
        Voir le planning complet
        <ChevronRight 
          size={18} 
          className={`ml-auto transition-transform ${showSchedule ? "rotate-90" : ""}`} 
        />
      </Button>

      {/* Full Schedule */}
      {showSchedule && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-3"
        >
          {schedule.map((phase) => (
            <div
              key={phase.phase}
              data-testid={`schedule-phase-${phase.phase}`}
              className={`medication-phase ${
                phase.phase === status?.current_phase ? "current" : ""
              } ${phase.phase < status?.current_phase ? "completed" : ""}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-[#2C3E50]">
                  Phase {phase.phase}
                  {phase.phase === status?.current_phase && (
                    <span className="ml-2 text-xs bg-[#A3E4D7] px-2 py-0.5 rounded-full">
                      Actuelle
                    </span>
                  )}
                </span>
              </div>
              <p className="text-sm text-[#7F8C8D] mb-2">
                {phase.description}
              </p>
              <div className="flex gap-4 text-sm">
                <span className="text-[#2C3E50]">
                  <strong>20h:</strong> {phase.dose_20h}
                </span>
                <span className="text-[#2C3E50]">
                  <strong>Minuit:</strong> {phase.dose_minuit}
                </span>
              </div>
              <div className="mt-2 text-right">
                <span className="text-xs font-semibold text-[#A3E4D7]">
                  Total: {phase.total}
                </span>
              </div>
            </div>
          ))}
        </motion.div>
      )}

      {/* Encouragement */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center py-4"
      >
        <p className="text-[#7F8C8D] text-sm">
          Chaque jour compte. Tu avances à ton rythme, et c'est parfait.
        </p>
      </motion.div>
    </div>
  );
}
